import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  BookOpen,
  Plus,
  Users,
  Star,
  Edit,
  Eye,
  Loader2,
  Upload,
  DollarSign,
  Banknote,
  CheckCircle,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";

const InstructorDashboard = () => {
  const { user, token } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [earnings, setEarnings] = useState(null);

  const [newCourse, setNewCourse] = useState({
    title: "",
    description: "",
    short_description: "",
    thumbnail: "",
    price: 49.99,
    category: "devops",
    level: "beginner",
    tags: [],
  });

  useEffect(() => {
    fetchDashboard();
    fetchEarnings();
  }, []);

  const fetchEarnings = async () => {
    try {
      const res = await axios.get(`${API}/instructor/earnings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEarnings(res.data);
    } catch { /* no earnings yet */ }
  };

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/instructor`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Veuillez sélectionner une image");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image trop volumineuse (max 5 Mo)");
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      setNewCourse((prev) => ({ ...prev, thumbnail: `${API}${response.data.url}` }));
      toast.success("Image téléversée!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du téléversement");
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  const handleCreateCourse = async () => {
    if (!newCourse.title || !newCourse.description) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API}/courses`, newCourse, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Cours créé avec succès!");
      setShowCreateDialog(false);
      setNewCourse({
        title: "",
        description: "",
        short_description: "",
        thumbnail: "",
        price: 49.99,
        category: "devops",
        level: "beginner",
        tags: [],
      });
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };

  const handlePublishCourse = async (courseId) => {
    try {
      await axios.patch(
        `${API}/courses/${courseId}/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Cours publié!");
      fetchDashboard();
    } catch (error) {
      toast.error("Erreur lors de la publication");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const { courses = [], total_courses, total_students, published_courses } = dashboardData || {};

  return (
    <div className="min-h-screen bg-background" data-testid="instructor-dashboard">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold">Espace Instructeur</h1>
            <p className="text-muted-foreground">Gérez vos cours et suivez vos étudiants</p>
          </div>
          
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="glow-green" data-testid="create-course-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau cours
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Créer un nouveau cours</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Titre du cours *</Label>
                  <Input
                    value={newCourse.title}
                    onChange={(e) => setNewCourse({ ...newCourse, title: e.target.value })}
                    placeholder="Docker pour débutants"
                    data-testid="course-title-input"
                  />
                </div>
                <div>
                  <Label>Description courte *</Label>
                  <Input
                    value={newCourse.short_description}
                    onChange={(e) => setNewCourse({ ...newCourse, short_description: e.target.value })}
                    placeholder="Apprenez Docker de zéro"
                  />
                </div>
                <div>
                  <Label>Description complète *</Label>
                  <Textarea
                    value={newCourse.description}
                    onChange={(e) => setNewCourse({ ...newCourse, description: e.target.value })}
                    placeholder="Description détaillée du cours..."
                    rows={4}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Catégorie</Label>
                    <Select
                      value={newCourse.category}
                      onValueChange={(v) => setNewCourse({ ...newCourse, category: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="docker">Docker</SelectItem>
                        <SelectItem value="kubernetes">Kubernetes</SelectItem>
                        <SelectItem value="aws">AWS</SelectItem>
                        <SelectItem value="azure">Azure</SelectItem>
                        <SelectItem value="devops">DevOps</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Niveau</Label>
                    <Select
                      value={newCourse.level}
                      onValueChange={(v) => setNewCourse({ ...newCourse, level: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="beginner">Débutant</SelectItem>
                        <SelectItem value="intermediate">Intermédiaire</SelectItem>
                        <SelectItem value="advanced">Avancé</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Prix ($)</Label>
                    <Input
                      type="number"
                      value={newCourse.price}
                      onChange={(e) => setNewCourse({ ...newCourse, price: parseFloat(e.target.value) })}
                      min={0}
                      step={0.01}
                    />
                  </div>
                  <div>
                    <Label>Image de couverture</Label>
                    <div className="flex items-center gap-3 mt-1">
                      <input
                        id="new-course-image-input"
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        disabled={uploadingImage}
                        onClick={() => document.getElementById("new-course-image-input").click()}
                      >
                        {uploadingImage ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        {newCourse.thumbnail ? "Changer" : "Choisir"}
                      </Button>
                      {newCourse.thumbnail && (
                        <img
                          src={newCourse.thumbnail}
                          alt="preview"
                          className="w-12 h-12 rounded object-cover border border-border"
                        />
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                    Annuler
                  </Button>
                  <Button onClick={handleCreateCourse} disabled={creating} data-testid="submit-course-btn">
                    {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer le cours"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{total_courses}</p>
                <p className="text-sm text-muted-foreground">Cours créés</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-secondary/20 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{total_students}</p>
                <p className="text-sm text-muted-foreground">Étudiants</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-accent/20 rounded-lg flex items-center justify-center">
                <Eye className="w-5 h-5 text-accent" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{published_courses}</p>
                <p className="text-sm text-muted-foreground">Publiés</p>
              </div>
            </div>
          </div>
        </div>

        {/* Courses List */}
        <div className="bg-card border border-border rounded-lg">
          <div className="p-4 border-b border-border">
            <h2 className="font-heading font-bold">Mes cours</h2>
          </div>
          
          {courses.length === 0 ? (
            <div className="p-12 text-center">
              <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-bold mb-2">Aucun cours créé</h3>
              <p className="text-muted-foreground mb-4">
                Commencez par créer votre premier cours.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {courses.map((course) => (
                <div key={course.course_id} className="p-4 flex items-center gap-4">
                  <img
                    src={course.thumbnail || "https://images.unsplash.com/photo-1667372393119-3d4c48d07fc9?w=200"}
                    alt={course.title}
                    className="w-24 h-16 object-cover rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold truncate">{course.title}</h3>
                      {course.is_published ? (
                        <Badge className="bg-primary/20 text-primary">Publié</Badge>
                      ) : (
                        <Badge variant="outline">Brouillon</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {course.students_count} étudiants
                      </span>
                      <span className="flex items-center gap-1">
                        <Star className="w-3 h-3" />
                        {course.rating.toFixed(1)}
                      </span>
                      <span>${course.price}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {!course.is_published && (
                      <Button
                        size="sm"
                        onClick={() => handlePublishCourse(course.course_id)}
                        data-testid={`publish-${course.course_id}`}
                      >
                        Publier
                      </Button>
                    )}
                    <Button variant="outline" size="sm">
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Earnings section */}
        {earnings && (
          <div className="mt-8">
            <h2 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary" />
              Mes revenus
            </h2>
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading font-bold">${earnings.total.toFixed(2)}</p>
                    <p className="text-sm text-muted-foreground">Total gagné</p>
                  </div>
                </div>
              </div>
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                    <Banknote className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading font-bold">${earnings.pending.toFixed(2)}</p>
                    <p className="text-sm text-muted-foreground">En attente</p>
                  </div>
                </div>
              </div>
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading font-bold">${earnings.paid.toFixed(2)}</p>
                    <p className="text-sm text-muted-foreground">Versé</p>
                  </div>
                </div>
              </div>
            </div>

            {earnings.earnings.length > 0 && (
              <div className="bg-card border border-border rounded-lg">
                <div className="p-4 border-b border-border">
                  <h3 className="font-heading font-bold">Historique des transactions</h3>
                </div>
                <div className="divide-y divide-border">
                  {earnings.earnings.slice(0, 20).map((e) => (
                    <div key={e.earning_id} className="p-4 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{e.course_title}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(e.created_at).toLocaleDateString("fr-FR")} · Brut: ${e.gross_amount.toFixed(2)} · TVA: -${e.vat_amount.toFixed(2)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-primary">${e.instructor_earning.toFixed(2)}</p>
                        <Badge className={e.status === "paid" ? "bg-green-500/20 text-green-400 border-green-500/30" : "bg-yellow-500/20 text-yellow-400 border-yellow-500/30"}>
                          {e.status === "paid" ? "Payé" : "En attente"}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default InstructorDashboard;
